export const WIDGET_TYPES = [
  'perf.scorecard',
  'perf.northStar',
  'perf.highlightsCard',
  'perf.situation',
  'perf.outletTable',
  'live.curve',
  'live.gantt',
  'live.ribKpis',
  'live.segbar',
  'live.occupancyRing',
  'live.briefGroups',
  'live.briefArrivals',
  'live.briefAttention',
  'live.recovery',
  'live.sell',
  'live.weather',
  'horizon.week',
  'horizon.hero',
  'horizon.signals',
] as const;

export type CanonicalWidgetType = (typeof WIDGET_TYPES)[number];

export type WidgetFamily = 'performance' | 'live' | 'horizon' | 'legacy';

export type WidgetPreset = 'S' | 'M' | 'L' | 'full';

export const WIDGET_PRESETS: WidgetPreset[] = ['S', 'M', 'L', 'full'];

export type WidgetSpec = {
  type: CanonicalWidgetType;
  title: string;
  params?: Record<string, string>;
};

export const WIDGET_TYPE_SET = new Set<string>(WIDGET_TYPES);

export const SCORECARD_METRIC_KEYS = [
  'covers',
  'reservations',
  'noshow',
  'cancel',
  'occupancy',
  'brainAutoPct',
  'turns',
  'seatTime',
  'sellitems',
  'revenue',
  'avgCheck',
  'revpash',
] as const;

const SCORECARD_METRIC_ALIASES: Record<string, string> = {
  guests: 'covers',
  guest: 'covers',
  totalcovers: 'covers',
  served: 'covers',
  servedguests: 'covers',
  booked: 'reservations',
  bookings: 'reservations',
  reservation: 'reservations',
  noshows: 'noshow',
  noshowrate: 'noshow',
  cancels: 'cancel',
  cancellation: 'cancel',
  cancellations: 'cancel',
  cancelrate: 'cancel',
  occ: 'occupancy',
  occupancypct: 'occupancy',
  brain: 'brainAutoPct',
  brainauto: 'brainAutoPct',
  autopct: 'brainAutoPct',
  dwell: 'seatTime',
  seattime: 'seatTime',
  sell: 'sellitems',
  sellitem: 'sellitems',
  pos: 'revenue',
  posrevenue: 'revenue',
  check: 'avgCheck',
  avgcheck: 'avgCheck',
  averagecheck: 'avgCheck',
};

export const RIB_KPI_COLUMNS = ['soFar', 'rightNow', 'stillToCome'] as const;
const RIB_KPI_ALIASES: Record<string, string> = {
  sofar: 'soFar',
  today: 'soFar',
  past: 'soFar',
  rightnow: 'rightNow',
  now: 'rightNow',
  live: 'rightNow',
  stilltocome: 'stillToCome',
  future: 'stillToCome',
  incoming: 'stillToCome',
};

export const HIGHLIGHT_VARIANTS = ['attention', 'leading', 'pattern'] as const;
const HIGHLIGHT_ALIASES: Record<string, string> = {
  lowlight: 'attention',
  risk: 'attention',
  worst: 'attention',
  highlight: 'leading',
  win: 'leading',
  best: 'leading',
  nugget: 'pattern',
  insight: 'pattern',
};

function compactParamKey(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]/g, '');
}

function resolveMappedParam(
  raw: string | undefined,
  allowed: readonly string[],
  aliases: Record<string, string>,
  fallback: string,
): string {
  const trimmed = (raw ?? fallback).trim() || fallback;
  const exact = allowed.find(key => key.toLowerCase() === trimmed.toLowerCase());
  if (exact) return exact;
  const mapped = aliases[compactParamKey(trimmed)] ?? trimmed;
  return allowed.includes(mapped) ? mapped : fallback;
}

export function resolveScorecardMetricKey(raw: string | undefined): string {
  const trimmed = (raw ?? 'covers').trim() || 'covers';
  const exact = SCORECARD_METRIC_KEYS.find(
    key => key.toLowerCase() === trimmed.toLowerCase(),
  );
  if (exact) return exact;
  const compact = compactParamKey(trimmed);
  const aliased = SCORECARD_METRIC_ALIASES[compact];
  if (aliased) return aliased;
  return (
    SCORECARD_METRIC_KEYS.find(key => compactParamKey(key) === compact) ?? trimmed
  );
}

export function resolveRibKpiColumn(raw: string | undefined): string {
  return resolveMappedParam(raw, RIB_KPI_COLUMNS, RIB_KPI_ALIASES, 'soFar');
}

export function resolveHighlightsVariant(raw: string | undefined): string {
  return resolveMappedParam(raw, HIGHLIGHT_VARIANTS, HIGHLIGHT_ALIASES, 'pattern');
}

function normalizeWidgetParams(
  type: CanonicalWidgetType,
  params: Record<string, string>,
): Record<string, string> {
  const next = { ...params };
  if (type === 'perf.scorecard') {
    next.metricKey = resolveScorecardMetricKey(next.metricKey);
  } else if (type === 'live.ribKpis') {
    next.column = resolveRibKpiColumn(next.column);
  } else if (type === 'perf.highlightsCard') {
    next.variant = resolveHighlightsVariant(next.variant);
  }
  return next;
}

export function isCanonicalWidgetType(value: string): value is CanonicalWidgetType {
  return WIDGET_TYPE_SET.has(value);
}

export function widgetFamilyOf(type: CanonicalWidgetType): WidgetFamily {
  if (type.startsWith('live.')) return 'live';
  if (type.startsWith('horizon.')) return 'horizon';
  return 'performance';
}

export function widgetParamKey(spec: WidgetSpec): string {
  const metric = spec.params?.metricKey;
  const column = spec.params?.column;
  const variant = spec.params?.variant;
  const outletId = spec.params?.outletId;
  const patternKey = spec.params?.patternKey;
  return [spec.type, metric, column, variant, outletId, patternKey]
    .filter(Boolean)
    .join(':');
}

export function coerceWidgetSpecs(raw: unknown): WidgetSpec[] {
  if (!Array.isArray(raw)) return [];
  const out: WidgetSpec[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const rec = item as Record<string, unknown>;
    const type = typeof rec.type === 'string' ? rec.type : '';
    if (!isCanonicalWidgetType(type)) continue;
    const title =
      typeof rec.title === 'string' && rec.title.trim()
        ? rec.title.trim()
        : type;
    const params: Record<string, string> = {};
    if (rec.params && typeof rec.params === 'object' && !Array.isArray(rec.params)) {
      for (const [key, value] of Object.entries(rec.params as Record<string, unknown>)) {
        if (typeof value === 'string' && value.trim()) params[key] = value.trim();
        else if (typeof value === 'number' && Number.isFinite(value)) {
          params[key] = String(value);
        }
      }
    }
    const normalized = normalizeWidgetParams(type, params);
    out.push({
      type,
      title,
      params: Object.keys(normalized).length ? normalized : undefined,
    });
  }
  return out;
}
